import { useState } from 'react';
import { getDesktop } from '@/desktop/desktop';

type Mode = 'skjv' | 'local' | 'remote';
type TestState =
  | { kind: 'idle' }
  | { kind: 'testing' }
  | { kind: 'ok'; models: string[] }
  | { kind: 'fail'; error: string };

/**
 * SKJV Desktop first run: where does inference live? (PRD §6a — asked
 * before anything else; the reader works either way.) Saving persists
 * desktop settings, starts the managed API, and reloads into it.
 */
export function FirstRunSetup() {
  const desktop = getDesktop();
  const [mode, setMode] = useState<Mode>('skjv');
  const [url, setUrl] = useState('');
  const [test, setTest] = useState<TestState>({ kind: 'idle' });
  const [saving, setSaving] = useState(false);
  const [storageRoot, setStorageRoot] = useState<string | null>(null);
  if (!desktop) return null;

  const canSave = mode !== 'remote' || url.trim().length > 0;
  const shownRoot = storageRoot ?? desktop.storageRoot;

  const chooseFolder = async () => {
    const picked = await desktop.pickDirectory();
    if (picked) setStorageRoot(picked);
  };

  const runTest = async () => {
    setTest({ kind: 'testing' });
    const result = await desktop.testOllama(url.trim());
    setTest(
      result.ok
        ? { kind: 'ok', models: result.models ?? [] }
        : { kind: 'fail', error: result.error ?? 'unreachable' },
    );
  };

  const save = async () => {
    setSaving(true);
    const inference =
      mode === 'skjv'
        ? ({ source: 'skjv' } as const)
        : mode === 'local'
          ? ({ source: 'local' } as const)
          : ({ source: 'remote', url: url.trim() } as const);
    await desktop.saveSettings({
      inference,
      ...(storageRoot ? { storageRoot } : {}),
    });
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="first-run-setup">
      <div className="mx-4 w-full max-w-md rounded-2xl border border-stone-200 bg-white p-6 text-stone-800 shadow-2xl dark:border-stone-700/50 dark:bg-stone-800 dark:text-stone-100">
        <h2 className="text-lg font-semibold">Where should AI features run?</h2>
        <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
          Reading, Strong&apos;s, and studies work either way. You can change this later.
        </p>

        <div className="mt-4 space-y-2">
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-300 p-3 hover:bg-stone-100 dark:border-white/10 dark:hover:bg-white/5">
            <input type="radio" name="inference" checked={mode === 'skjv'} onChange={() => setMode('skjv')} data-testid="option-skjv" className="mt-1" />
            <span>
              <span className="font-medium">On skjv.ai (recommended)</span>
              <span className="block text-sm text-stone-500 dark:text-stone-400">
                Nothing to set up — AI runs on skjv.ai and needs an internet connection
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-300 p-3 hover:bg-stone-100 dark:border-white/10 dark:hover:bg-white/5">
            <input type="radio" name="inference" checked={mode === 'local'} onChange={() => setMode('local')} data-testid="option-local" className="mt-1" />
            <span>
              <span className="font-medium">On this Mac</span>
              <span className="block text-sm text-stone-500 dark:text-stone-400">Uses Ollama at localhost:11434</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-300 p-3 hover:bg-stone-100 dark:border-white/10 dark:hover:bg-white/5">
            <input type="radio" name="inference" checked={mode === 'remote'} onChange={() => setMode('remote')} data-testid="option-remote" className="mt-1" />
            <span className="flex-1">
              <span className="font-medium">On an Ollama server</span>
              <span className="block text-sm text-stone-500 dark:text-stone-400">A machine on your network running Ollama</span>
              {mode === 'remote' && (
                <span className="mt-2 flex gap-2">
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="http://your-server:11434"
                    data-testid="ollama-url"
                    className="w-full rounded-md border border-stone-300 bg-stone-50 px-2 py-1.5 text-sm text-stone-800 outline-none focus:border-cyan-400 dark:border-white/15 dark:bg-stone-900 dark:text-stone-100"
                  />
                  <button type="button" onClick={runTest} disabled={!url.trim() || test.kind === 'testing'} data-testid="test-ollama" className="rounded-md border border-stone-300 px-3 py-1.5 text-sm hover:bg-stone-100 dark:border-white/15 dark:hover:bg-white/10 disabled:opacity-40">
                    Test
                  </button>
                </span>
              )}
              {mode === 'remote' && test.kind === 'ok' && (
                <span className="mt-2 block text-sm text-emerald-600 dark:text-emerald-400" data-testid="test-result">
                  Connected — {test.models.length} model{test.models.length === 1 ? '' : 's'} available
                </span>
              )}
              {mode === 'remote' && test.kind === 'fail' && (
                <span className="mt-2 block text-sm text-rose-600 dark:text-rose-400" data-testid="test-result">
                  Could not reach it: {test.error}
                </span>
              )}
            </span>
          </label>
        </div>

        <div className="mt-4 rounded-lg border border-stone-300 p-3 dark:border-white/10">
          <span className="font-medium">Where should your studies live?</span>
          <span className="mt-1 block break-all text-sm text-stone-500 dark:text-stone-400" data-testid="storage-root-path">
            {shownRoot}
          </span>
          <span className="mt-1 block text-xs text-stone-500">
            Pick a folder your backups cover — Documents, iCloud Drive, wherever you keep what matters.
          </span>
          <button type="button" onClick={chooseFolder} data-testid="choose-storage-root" className="mt-2 rounded-md border border-stone-300 px-3 py-1.5 text-sm hover:bg-stone-100 dark:border-white/15 dark:hover:bg-white/10">
            Choose folder…
          </button>
        </div>

        <button type="button" onClick={save} disabled={!canSave || saving} data-testid="save-setup" className="mt-5 w-full rounded-lg bg-cyan-600 py-2 font-medium text-white hover:bg-cyan-500 disabled:opacity-40">
          {saving ? 'Starting…' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
